import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, firestore, googleProvider, storage } from './firebase/config';
import { createUserWithEmailAndPassword, signInWithPopup, updateProfile } from 'firebase/auth';
import { doc, setDoc, getDoc, collection } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  saveUserExperience, 
  saveUserJobPreferences, 
  createStoragePath, 
  checkUserProfile,
  testCollectionAccess 
} from './firebase/adapters';

const RegularUserSignUp = () => {
  const navigate = useNavigate();
  
  // Basic auth info
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  
  // Profile info (step 1)
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [aboutMe, setAboutMe] = useState('');
  const [education, setEducation] = useState('');
  const [languages, setLanguages] = useState([]);
  const [workLocation, setWorkLocation] = useState('');
  
  // Adding common universities for autocomplete
  const popularSchools = [
    'Harvard University',
    'Stanford University',
    'MIT',
    'Yale University',
    'Princeton University',
    'Columbia University',
    'University of California, Berkeley',
    'University of Chicago',
    'New York University',
    'Cornell University',
    'UCLA',
    'University of Michigan',
    'Duke University',
    'University of Pennsylvania'
  ];
  
  // For language selection
  const commonLanguages = [
    'English',
    'Spanish',
    'Mandarin',
    'Cantonese',
    'French',
    'German',
    'Japanese',
    'Korean',
    'Italian',
    'Russian',
    'Portuguese',
    'Arabic'
  ];
  
  // Education degrees
  const educationDegrees = [
    'High School Diploma',
    'Associate Degree',
    'Bachelor\'s Degree',
    'Master\'s Degree',
    'PhD',
    'Trade School Certificate',
    'Other'
  ];
  
  // Experience info (step 2)
  const [jobTitle, setJobTitle] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [workStartDate, setWorkStartDate] = useState('');
  const [workEndDate, setWorkEndDate] = useState('');
  const [isCurrentJob, setIsCurrentJob] = useState(false);
  const [skills, setSkills] = useState([]);
  
  // Common hospitality and restaurant skills
  const commonSkills = [
    'Cooking', 'Food Preparation', 'Bartending', 'Serving', 'Hosting',
    'Customer Service', 'Cash Handling', 'Food Safety', 'Cleaning',
    'Inventory Management', 'Team Leadership', 'POS Systems',
    'Menu Knowledge', 'Wine Knowledge', 'Barista Skills'
  ];
  
  // Job preferences (step 3)
  const [desiredJobs, setDesiredJobs] = useState([]);
  const [expectedRate, setExpectedRate] = useState('');
  const [availability, setAvailability] = useState('');
  const [startDate, setStartDate] = useState('');
  
  // Common desired job types in hospitality
  const commonJobTypes = [
    'Server', 'Bartender', 'Host/Hostess', 'Cook', 'Chef', 
    'Barback', 'Busser', 'Dishwasher', 'Manager', 'Sous Chef',
    'Line Cook', 'Prep Cook', 'Baker', 'Cashier', 'Delivery Driver'
  ];
  
  // Availability options
  const availabilityOptions = [
    { value: 'immediate', label: 'Available immediately' },
    { value: 'oneWeek', label: 'Within 1 week' },
    { value: 'twoWeeks', label: 'Within 2 weeks' },
    { value: 'oneMonth', label: 'Within 1 month' },
    { value: 'casual', label: 'Available for casual work' },
    { value: 'notAvailable', label: 'Not available right now' }
  ];
  
  // UI state
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);

  // Handle account creation (step 1)
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile display name
      await updateProfile(userCredential.user, {
        displayName: name
      });
      
      // Store basic user data in Firestore
      await setDoc(doc(firestore, 'users', userCredential.user.uid), {
        firstName: name.split(' ')[0] || '',
        lastName: name.split(' ').slice(1).join(' ') || '',
        email: email,
        role: 'regular_user',
        createdAt: new Date()
      });
      
      console.log('User account created:', userCredential.user);
      setAccountCreated(true);
      // Proceed to next step
      setCurrentStep(2);
    } catch (error) {
      console.error('Error signing up:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle profile information submission (step 2)
  const handleProfileSubmit = async () => {
    if (!accountCreated) return;
    
    setLoading(true);
    try {
      const profileData = {
        firstName: name.split(' ')[0] || '',
        lastName: name.split(' ').slice(1).join(' ') || '',
        aboutMe,
        education,
        languages,
        workLocation,
        updatedAt: new Date()
      };
      
      // Upload profile image if provided - UPDATED to match security rules pattern
      if (profileImage) {
        try {
          // Format path to match security rules: userId_filename format
          const storageRef = ref(storage, `userProfilePhoto/${auth.currentUser.uid}_profile`);
          await uploadBytes(storageRef, profileImage);
          const downloadURL = await getDownloadURL(storageRef);
          
          profileData.photoURL = downloadURL;
          
          // Update user profile photo URL
          await updateProfile(auth.currentUser, {
            photoURL: downloadURL
          });
        } catch (error) {
          console.error("Error uploading profile image:", error);
          setError("Failed to upload profile image. Please try again.");
          setLoading(false);
          return;
        }
      }
      
      // Update user profile in Firestore
      await setDoc(doc(firestore, 'userProfiles', auth.currentUser.uid), profileData, { merge: true });
      
      console.log('Profile updated successfully');
      setCurrentStep(3);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle experience submission (step 3) - Using adapter function
  const handleExperienceSubmit = async () => {
    if (!accountCreated) {
      console.error("❌ Account not created, cannot submit experience");
      return;
    }
    
    // Verify user is authenticated
    if (!auth.currentUser) {
      setError('Authentication error. Please sign in again.');
      return;
    }
    
    setLoading(true);
    try {
      console.log('⏳ Attempting to update experience for user:', auth.currentUser.uid);
      
      // Format the work period
      let formattedWorkPeriod = '';
      if (workStartDate) {
        const startDate = new Date(workStartDate);
        formattedWorkPeriod = startDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        
        if (!isCurrentJob && workEndDate) {
          const endDate = new Date(workEndDate);
          formattedWorkPeriod += ` - ${endDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
        } else if (isCurrentJob) {
          formattedWorkPeriod += ' - Present';
        }
      }
      
      // Create experience data
      const experienceData = {
        jobTitle,
        jobDescription,
        businessName,
        workPeriod: formattedWorkPeriod,
        startDate: workStartDate ? new Date(workStartDate) : null,
        endDate: (!isCurrentJob && workEndDate) ? new Date(workEndDate) : null,
        isCurrentJob,
        skills,
        _debug_timestamp: new Date().toString() // Add timestamp for debugging
      };
      
      console.log('📋 Experience data to save:', experienceData);
      
      // First try using the adapter function for multiple collections
      let success = await saveUserExperience(auth.currentUser.uid, experienceData);
      
      if (!success) {
        console.log('⚠️ Adapter function failed to save experience, trying direct save to userProfiles');
        
        // Direct save to userProfiles as a last resort
        const profileRef = doc(firestore, 'userProfiles', auth.currentUser.uid);
        
        // Use multiple field names to increase chances of success
        await setDoc(profileRef, {
          experience: experienceData,
          workExperience: experienceData,
          employmentHistory: experienceData,
          _debug_experience_direct_save: true,
          _debug_save_timestamp: new Date().toString()
        }, { merge: true });
        
        // Verify the data was written correctly
        const docSnapshot = await getDoc(profileRef);
        if (docSnapshot.exists()) {
          const savedData = docSnapshot.data();
          console.log('📋 userProfiles document after direct save:', savedData);
          
          // Check for any of our alternative field names
          if (savedData.experience || savedData.workExperience || savedData.employmentHistory) {
            console.log('✅ Confirmed experience saved through direct write to userProfiles');
            success = true;
          } else {
            console.error('❌ Failed to save experience directly to userProfiles');
          }
        }
      }
      
      if (success) {
        console.log('✅ Experience updated successfully');
        setCurrentStep(4);
      } else {
        throw new Error('Failed to save experience data after multiple attempts');
      }
    } catch (error) {
      console.error('❌ Error updating experience:', error);
      console.log('Error code:', error.code, 'Error message:', error.message);
      setError(`Error: ${error.message}. Please try again later.`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle job preferences submission (final step) - Using adapter function
  const handleJobPreferencesSubmit = async () => {
    if (!accountCreated) {
      console.error("❌ Account not created, cannot submit job preferences");
      return;
    }
    
    // Verify user is authenticated
    if (!auth.currentUser) {
      setError('Authentication error. Please sign in again.');
      return;
    }
    
    setLoading(true);
    try {
      console.log('⏳ Attempting to update job preferences for user:', auth.currentUser.uid);
      
      // Create preferences data object
      const preferencesData = {
        desiredJobs,
        expectedRate,
        availability,
        startDate: startDate || null,
        _debug_timestamp: new Date().toString() // Add timestamp for debugging
      };
      
      console.log('📋 Job preferences data to save:', preferencesData);
      
      // First, try saving through the adapter function that attempts multiple collections
      let success = await saveUserJobPreferences(auth.currentUser.uid, preferencesData);
      
      if (!success) {
        console.log('⚠️ Adapter function failed to save job preferences, trying direct save to userProfiles');
        
        // Direct save to userProfiles as a last resort
        const profileRef = doc(firestore, 'userProfiles', auth.currentUser.uid);
        
        // Make sure we use a different property name than what might have been used in the adapter
        // This ensures we're not overriding any existing data structure
        await setDoc(profileRef, {
          jobPreferences: preferencesData,
          preferences: preferencesData, // Try an alternative field name
          isProfileComplete: true,
          completedAt: new Date(),
          _debug_job_preferences_direct_save: true
        }, { merge: true });
        
        // Verify the data was written correctly
        const docSnapshot = await getDoc(profileRef);
        if (docSnapshot.exists()) {
          const savedData = docSnapshot.data();
          console.log('📋 userProfiles document after direct save:', savedData);
          if (savedData.jobPreferences || savedData.preferences) {
            console.log('✅ Confirmed job preferences saved through direct write to userProfiles');
            success = true;
          } else {
            console.error('❌ Failed to save job preferences directly to userProfiles');
          }
        }
      }
      
      if (success) {
        console.log('✅ Job preferences updated successfully');
        
        // Navigate to dashboard
        setTimeout(() => {
          navigate('/dashboard/home');
        }, 500);
      } else {
        throw new Error('Failed to save job preferences after multiple attempts');
      }
    } catch (error) {
      console.error('❌ Error updating job preferences:', error);
      console.log('Error code:', error.code, 'Error message:', error.message);
      setError(`Error: ${error.message}. Please try again later.`);
    } finally {
      setLoading(false);
    }
  };
  
  // Move to next step
  const handleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };
  
  // Go back to previous step
  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };
  
  // Handle file selection for profile image
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setProfileImage(e.target.files[0]);
      setProfileImageUrl(URL.createObjectURL(e.target.files[0]));
    }
  };
  
  // Add or remove a skill
  const handleSkillToggle = (skill) => {
    if (skills.includes(skill)) {
      setSkills(skills.filter(item => item !== skill));
    } else {
      setSkills([...skills, skill]);
    }
  };
  
  // Add or remove a job type
  const handleJobToggle = (job) => {
    if (desiredJobs.includes(job)) {
      setDesiredJobs(desiredJobs.filter(item => item !== job));
    } else {
      setDesiredJobs([...desiredJobs, job]);
    }
  };
  
  // Add or remove a language
  const handleLanguageToggle = (language) => {
    if (languages.includes(language)) {
      setLanguages(languages.filter(item => item !== language));
    } else {
      setLanguages([...languages, language]);
    }
  };
  
  // Handle form submission based on current step
  const handleSubmit = (e) => {
    e.preventDefault();
    
    switch (currentStep) {
      case 1:
        handleCreateAccount(e);
        break;
      case 2:
        handleProfileSubmit();
        break;
      case 3:
        handleExperienceSubmit();
        break;
      case 4:
        handleJobPreferencesSubmit();
        break;
      default:
        break;
    }
  };
  
  // Handle Google sign up
  const handleGoogleSignUp = async () => {
    setError('');
    setLoading(true);
    
    try {
      // Sign in with Google
      const result = await signInWithPopup(auth, googleProvider);
      
      // Check if this is a new user
      const isNewUser = result._tokenResponse.isNewUser;
      
      if (isNewUser) {
        // Create user record in Firestore
        await setDoc(doc(firestore, 'users', result.user.uid), {
          firstName: result.user.displayName?.split(' ')[0] || '',
          lastName: result.user.displayName?.split(' ').slice(1).join(' ') || '',
          email: result.user.email,
          role: 'regular_user',
          createdAt: new Date()
        });
        
        // If user has a Google profile image, save it to our storage with correct path format
        if (result.user.photoURL) {
          try {
            // Fetch the image from Google
            const response = await fetch(result.user.photoURL);
            const blob = await response.blob();
            
            // Upload it to our storage with correct path format (userId_filename)
            const storageRef = ref(storage, `userProfilePhoto/${result.user.uid}_google_profile`);
            await uploadBytes(storageRef, blob);
            const downloadURL = await getDownloadURL(storageRef);
            
            // Save the new URL to the user profile
            await setDoc(doc(firestore, 'userProfiles', result.user.uid), {
              photoURL: downloadURL,
              updatedAt: new Date()
            }, { merge: true });
          } catch (error) {
            console.error('Error saving Google profile image:', error);
            // Continue even if image save fails
          }
        }
        
        // Set initial state and move to profile completion
        setName(result.user.displayName || '');
        setEmail(result.user.email || '');
        setProfileImageUrl(result.user.photoURL || '');
        setAccountCreated(true);
        setCurrentStep(2);
      } else {
        // Existing user, redirect to dashboard
        navigate('/dashboard/home');
      }
    } catch (error) {
      console.error('Error with Google sign up:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    navigate('/');
  };

  // Render the appropriate content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="name" className="block text-left text-gray-700">
                  Full Name<span className="text-orange-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="email" className="block text-left text-gray-700">
                  Email Address<span className="text-orange-500">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="password" className="block text-left text-gray-700">
                  Password<span className="text-orange-500">*</span>
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Choose a strong password"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
                <p className="text-xs text-gray-500">
                  Password must be at least 8 characters
                </p>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-md hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50 disabled:opacity-70"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
            
            <div className="relative flex items-center justify-center my-4">
              <div className="border-t border-gray-300 w-full"></div>
              <span className="bg-white px-3 text-sm text-gray-500 absolute">or</span>
            </div>
            
            <button
              type="button"
              onClick={handleGoogleSignUp}
              disabled={loading}
              className="w-full py-3 border border-gray-300 rounded-md flex items-center justify-center space-x-2 hover:bg-gray-50 transition-colors disabled:opacity-70"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span>Continue with Google</span>
            </button>
          </>
        );
      
      case 2:
        return (
          <>
            <div className="space-y-4 mt-6">
              <div className="space-y-2">
                <label htmlFor="profileImage" className="block text-left text-gray-700">
                  Profile Image
                </label>
                <div className="flex items-center space-x-4">
                  <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                    {profileImageUrl ? (
                      <img src={profileImageUrl} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    )}
                  </div>
                  <input
                    id="profileImage"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  <label htmlFor="profileImage" className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md cursor-pointer hover:bg-gray-300">
                    Upload Image
                  </label>
                </div>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="aboutMe" className="block text-left text-gray-700">
                  About Me
                </label>
                <textarea
                  id="aboutMe"
                  value={aboutMe}
                  onChange={(e) => setAboutMe(e.target.value)}
                  placeholder="Tell us a bit about yourself"
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              {/* Education with autocomplete */}
              <div className="space-y-2">
                <label htmlFor="education" className="block text-left text-gray-700">
                  Education
                </label>
                <div className="relative">
                  <input
                    id="education"
                    type="text"
                    value={education}
                    onChange={(e) => setEducation(e.target.value)}
                    placeholder="Your education background"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                    list="schools-list"
                  />
                  <datalist id="schools-list">
                    {popularSchools.map((school, index) => (
                      <option key={index} value={school} />
                    ))}
                  </datalist>
                </div>
                <div className="mt-2">
                  <label htmlFor="degree" className="block text-left text-sm text-gray-700">
                    Select Degree (Optional)
                  </label>
                  <select 
                    id="degree" 
                    className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  >
                    <option value="">Select a degree type</option>
                    {educationDegrees.map((degree, index) => (
                      <option key={index} value={degree}>{degree}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Languages with tag selection */}
              <div className="space-y-2">
                <label className="block text-left text-gray-700">
                  Languages
                </label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {commonLanguages.map((language) => (
                    <button
                      key={language}
                      type="button"
                      onClick={() => handleLanguageToggle(language)}
                      className={`px-3 py-1 text-sm rounded-md ${
                        languages.includes(language)
                          ? 'bg-orange-100 text-orange-700 border border-orange-300'
                          : 'bg-gray-100 text-gray-700 border border-gray-300'
                      }`}
                    >
                      {language}
                    </button>
                  ))}
                </div>
                <div className="mt-2 flex items-center">
                  {languages.length > 0 && (
                    <div className="text-sm text-gray-600">
                      Selected: {languages.join(', ')}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="workLocation" className="block text-left text-gray-700">
                  Preferred Work Location
                </label>
                <input
                  id="workLocation"
                  type="text"
                  value={workLocation}
                  onChange={(e) => setWorkLocation(e.target.value)}
                  placeholder="Where you prefer to work"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-md hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50 disabled:opacity-70"
                >
                  {loading ? 'Saving...' : 'Continue'}
                </button>
              </div>
            </div>
          </>
        );
      
      case 3:
        return (
          <>
            <div className="space-y-4 mt-6">
              <div className="space-y-2">
                <label htmlFor="jobTitle" className="block text-left text-gray-700">
                  Job Title<span className="text-orange-500">*</span>
                </label>
                <input
                  id="jobTitle"
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="Your job title"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="businessName" className="block text-left text-gray-700">
                  Business Name<span className="text-orange-500">*</span>
                </label>
                <input
                  id="businessName"
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Business or company name"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              {/* Work period with date pickers */}
              <div className="space-y-2">
                <label className="block text-left text-gray-700">
                  Work Period<span className="text-orange-500">*</span>
                </label>
                
                <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-4">
                  <div className="flex-1">
                    <label htmlFor="workStartDate" className="block text-left text-sm text-gray-600">
                      Start Date
                    </label>
                    <input
                      id="workStartDate"
                      type="date"
                      value={workStartDate}
                      onChange={(e) => setWorkStartDate(e.target.value)}
                      required
                      className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>
                  
                  {!isCurrentJob && (
                    <div className="flex-1">
                      <label htmlFor="workEndDate" className="block text-left text-sm text-gray-600">
                        End Date
                      </label>
                      <input
                        id="workEndDate"
                        type="date"
                        value={workEndDate}
                        onChange={(e) => setWorkEndDate(e.target.value)}
                        className="w-full mt-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                  )}
                </div>
                
                <div className="flex items-center mt-2">
                  <input
                    id="isCurrentJob"
                    type="checkbox"
                    checked={isCurrentJob}
                    onChange={(e) => setIsCurrentJob(e.target.checked)}
                    className="h-4 w-4 text-orange-500 border-gray-300 rounded focus:ring-orange-400"
                  />
                  <label htmlFor="isCurrentJob" className="ml-2 block text-sm text-gray-700">
                    This is my current job
                  </label>
                </div>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="jobDescription" className="block text-left text-gray-700">
                  Job Description
                </label>
                <textarea
                  id="jobDescription"
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="Describe your responsibilities and achievements"
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-left text-gray-700">
                  Skills
                </label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {commonSkills.map((skill) => (
                    <button
                      key={skill}
                      type="button"
                      onClick={() => handleSkillToggle(skill)}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        skills.includes(skill)
                          ? 'bg-orange-100 text-orange-700 border border-orange-300'
                          : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {skill}
                    </button>
                  ))}
                </div>
                {skills.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    <span className="text-sm text-gray-600">Selected skills:</span>
                    {skills.map((skill) => (
                      <span key={skill} className="inline-flex items-center px-2 py-1 bg-orange-50 text-xs text-orange-700 rounded">
                        {skill}
                        <button
                          type="button"
                          onClick={() => handleSkillToggle(skill)}
                          className="ml-1 text-orange-500 hover:text-orange-700"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-md hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50 disabled:opacity-70"
                >
                  {loading ? 'Saving...' : 'Continue'}
                </button>
              </div>
            </div>
          </>
        );
      
      case 4:
        return (
          <>
            <div className="space-y-4 mt-6">
              <div className="space-y-2">
                <label className="block text-left text-gray-700">
                  Desired Job Types
                </label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {commonJobTypes.map((job) => (
                    <button
                      key={job}
                      type="button"
                      onClick={() => handleJobToggle(job)}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        desiredJobs.includes(job)
                          ? 'bg-orange-100 text-orange-700 border border-orange-300'
                          : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {job}
                    </button>
                  ))}
                </div>
                {desiredJobs.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    <span className="text-sm text-gray-600">Selected jobs:</span>
                    {desiredJobs.map((job) => (
                      <span key={job} className="inline-flex items-center px-2 py-1 bg-orange-50 text-xs text-orange-700 rounded">
                        {job}
                        <button
                          type="button"
                          onClick={() => handleJobToggle(job)}
                          className="ml-1 text-orange-500 hover:text-orange-700"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <label htmlFor="expectedRate" className="block text-left text-gray-700">
                  Expected Rate/Salary
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">$</span>
                  <input
                    id="expectedRate"
                    type="number"
                    min="1"
                    value={expectedRate}
                    onChange={(e) => setExpectedRate(e.target.value)}
                    placeholder="25"
                    className="w-full pl-8 pr-16 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                  <select
                    className="absolute right-1 top-1 bg-gray-100 border-0 py-1 px-2 rounded text-gray-700 text-sm"
                    defaultValue="hourly"
                  >
                    <option value="hourly">per hour</option>
                    <option value="yearly">per year</option>
                  </select>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="block text-left text-gray-700">
                  Availability
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                  {availabilityOptions.map((option) => (
                    <div key={option.value} className="flex items-center">
                      <input
                        id={option.value}
                        type="radio"
                        name="availability"
                        value={option.value}
                        checked={availability === option.value}
                        onChange={() => setAvailability(option.value)}
                        className="h-4 w-4 text-orange-500 border-gray-300 focus:ring-orange-400"
                      />
                      <label htmlFor={option.value} className="ml-2 text-sm text-gray-700">
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              {availability === 'notAvailable' && (
                <div className="space-y-2">
                  <label htmlFor="startDate" className="block text-left text-gray-700">
                    Available from
                  </label>
                  <input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
              )}
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 py-3 px-4 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-md hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50 disabled:opacity-70"
                >
                  {loading ? 'Finalizing...' : 'Complete Setup'}
                </button>
              </div>
            </div>
          </>
        );
      
      default:
        return null;
    }
  };

  // Render step indicator for steps 2, 3, 4
  const renderStepIndicator = () => {
    if (currentStep === 1) return null;
    
    return (
      <div className="mb-8">
        <div className="flex justify-between items-center">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex flex-col items-center">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  currentStep === step 
                    ? 'bg-orange-500 text-white' 
                    : currentStep > step 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-200 text-gray-600'
                }`}
              >
                {currentStep > step ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  step
                )}
              </div>
              <span className="text-xs mt-1 text-gray-500">
                {step === 1 && 'Account'}
                {step === 2 && 'Profile'}
                {step === 3 && 'Experience'}
                {step === 4 && 'Preferences'}
              </span>
            </div>
          ))}
        </div>
        <div className="relative flex items-center justify-between mt-2">
          <div className="absolute left-0 right-0 h-1 bg-gray-200">
            <div 
              className="absolute h-1 bg-orange-500 transition-all"
              style={{ width: `${((currentStep - 1) / 3) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
    );
  };

  // Get step title based on current step
  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return "Create Your Account";
      case 2:
        return "Complete Your Profile";
      case 3:
        return "Work Experience";
      case 4:
        return "Job Preferences";
      default:
        return "";
    }
  };

  return (
    <div className="relative flex items-center justify-center min-h-screen w-full overflow-hidden bg-gradient-to-br from-orange-50 to-orange-100">
      {/* Background pattern */}
      <div className="absolute inset-0 grid grid-cols-4 grid-rows-3 gap-4 z-0">
        {Array(12).fill().map((_, i) => (
          <div key={i} className="rounded-lg bg-gradient-to-br from-orange-200 to-orange-100 opacity-50"></div>
        ))}
      </div>
      
      {/* Main container */}
      <div className="relative z-10 flex flex-row w-full max-w-4xl bg-white rounded-lg shadow-xl overflow-hidden">
        {/* Form side */}
        <div className="w-full md:w-1/2 p-8 md:p-12">
          <div className="flex items-center mb-8">
            <img 
              src="/asset/Logo/hireeLogo.png" 
              alt="Hiree Logo" 
              className="h-10 w-auto"
            />
            <span className="ml-3 text-xl text-gray-600">Create your account</span>
          </div>
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}
          
          {renderStepIndicator()}
          
          <h2 className="text-xl font-semibold text-gray-800 mb-4">{getStepTitle()}</h2>
          
          <form onSubmit={handleSubmit}>
            {renderStepContent()}
          </form>
          
          {currentStep === 1 && (
            <div className="mt-6 text-center text-sm">
              <span className="text-gray-600">Already have an account?</span>{' '}
              <Link to="/login" className="text-orange-500 hover:text-orange-600">
                Sign in
              </Link>
            </div>
          )}
        </div>
        
        {/* Video side - only shown on medium screens and larger */}
        <div className="hidden md:block md:w-1/2 relative">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 z-20 p-2 bg-white bg-opacity-70 rounded-full hover:bg-opacity-100"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <video autoPlay muted loop className="absolute inset-0 w-full h-full object-cover">
            <source src="/asset/SignInPage/video1.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        </div>
      </div>
    </div>
  );
};

export default RegularUserSignUp;
